// Push Notification Service
// Handles browser push notifications when the app is not focused

class PushNotificationService {
  private static instance: PushNotificationService;
  private permission: NotificationPermission = 'default';
  private isSupported: boolean = false;

  private constructor() {
    this.isSupported = 'Notification' in window;
    if (this.isSupported) {
      this.permission = Notification.permission;
    }
  }

  public static getInstance(): PushNotificationService {
    if (!PushNotificationService.instance) {
      PushNotificationService.instance = new PushNotificationService();
    }
    return PushNotificationService.instance;
  }

  // Request permission for push notifications
  public async requestPermission(): Promise<boolean> {
    if (!this.isSupported) {
      console.log('🔔 Push notifications not supported');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      
      console.log('🔔 Push notification permission:', permission);
      return permission === 'granted';
    } catch (error) {
      console.error('🔔 Error requesting push notification permission:', error);
      return false;
    }
  }

  // Check if we have permission
  public hasPermission(): boolean {
    return this.isSupported && this.permission === 'granted';
  }

  // Show a push notification
  public showNotification(title: string, options: NotificationOptions = {}): void {
    if (!this.hasPermission()) {
      console.log('🔔 No permission for push notifications');
      return;
    }

    try {
      const notification = new Notification(title, {
        icon: '/ascend-logo.png',
        badge: '/ascend-logo.png',
        tag: 'chat-message',
        requireInteraction: false,
        silent: false,
        ...options
      });

      // Handle notification click
      notification.onclick = () => {
        // Focus the window
        window.focus();
        
        // Close the notification
        notification.close();
        
        // Navigate to chat if it's a message notification
        if (options.data?.sender_id) {
          window.location.href = `/chat?sender=${options.data.sender_id}`;
        }
      };

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      console.log('🔔 Push notification shown:', title);
    } catch (error) {
      console.error('🔔 Error showing push notification:', error);
    }
  }

  // Show a message notification
  public showMessageNotification(senderName: string, message: string, senderId: string): void {
    this.showNotification(`New message from ${senderName}`, {
      body: message,
      data: { sender_id: senderId },
      tag: `message-${senderId}`,
      requireInteraction: false
    });
  }

  // Initialize the service
  public async initialize(): Promise<void> {
    if (!this.isSupported) {
      console.log('🔔 Push notifications not supported');
      return;
    }

    // Check if we already have permission
    if (this.permission === 'default') {
      console.log('🔔 Requesting push notification permission...');
      await this.requestPermission();
    }

    // Set up visibility change listener
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        console.log('🔔 App hidden - push notifications will be used');
      } else {
        console.log('🔔 App visible - in-app notifications will be used');
      }
    });
  }
}

export default PushNotificationService;
